import { useQuery } from "@apollo/client";
import { LIMIT_PAGINATION_QUANTITY } from "../../../constants/orders";
import {
  GetAllOrdersForStaffDocument,
  OrderStatus,
  OrderWhereInput,
} from "../../../generated/graphql";
import { useSocketEvents } from "./useSocketEvents";
import { useEffect, useState } from "react";
import { OrderItem } from "../types";
import { toApiSearch } from "../utils/orderSearch";

interface Props {
  filterQuery?: OrderWhereInput;
  search?: string;
  filteredStatuses?: OrderStatus[];
  page: number;
}

export const useOrdersList = ({
  filteredStatuses = [],
  filterQuery,
  search,
  page,
}: Props) => {
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [totalOrderCount, setTotalOrderCount] = useState(0);

  useSocketEvents({
    page,
    filteredStatuses,
    addNewOrder: newOrder => {
      setOrders(state => {
        if (state.find((order: OrderItem) => order.id === newOrder?.id)) {
          return state;
        }

        setTotalOrderCount(total => total + 1);

        if (state.length === LIMIT_PAGINATION_QUANTITY) {
          const lastState = [...state];
          lastState.pop();
          return [newOrder, ...lastState];
        }

        return [newOrder, ...state];
      });
    },
    removeOrder: orderToRemove => {
      setOrders(state => {
        const orderIndex = state.findIndex(
          order => order.id === orderToRemove.id
        );

        if (orderIndex >= 0) {
          const newState = [...state];
          newState.splice(orderIndex, 1);
          setTotalOrderCount(total => total - 1);
          return newState;
        }

        return state;
      });
    },
  });

  const { data, refetch } = useQuery(GetAllOrdersForStaffDocument, {
    variables: {
      skip: LIMIT_PAGINATION_QUANTITY * (page - 1),
      take: LIMIT_PAGINATION_QUANTITY,
      where: filterQuery,
      search: toApiSearch(search),
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setOrders(data?.getAllOrdersForStaff.orders || []);
    setTotalOrderCount(data?.getAllOrdersForStaff.totalCount ?? 0);
  }, [data]);

  return { orders, refetchOrders: refetch, totalOrderCount };
};
